import { useEffect, useMemo, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  fromIsoDateString,
  leadingZerosDateFormat,
} from "../../../common/helpers/formatDateTime";
import DateField from "../../../common/components/functional/dateField/DateField";
import FormGridRow from "../../../common/components/functional/formGridRow/FormGridRow";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import FormTable, {
  DataItem,
} from "../../../common/components/functional/formTable/FormTable";
import TitleOptional from "../../../common/components/functional/titleOptional/TitleOptional";
import { OrderTemplateFormValues } from "../types/types";
import SalaryLimitsForm from "./SalaryLimitsForm";

type Props = {
  periodStartDateIsRequired: boolean | null;
};

const SalaryLimits = ({ periodStartDateIsRequired }: Props) => {
  const [dialogState, setDialogState] = useState<{
    open: boolean;
    index: number | null;
  }>({ open: false, index: null });

  const { t } = useTranslation("order");
  const { control, watch, setValue, trigger } =
    useFormContext<OrderTemplateFormValues>();

  const { remove } = useFieldArray({
    control,
    name: "salaryLimits",
  });

  const salaryLimits = watch("salaryLimits");

  useEffect(() => {
    if (periodStartDateIsRequired === false) {
      setValue("periodStartDate", null);
    }
    trigger("periodStartDate");
  }, [periodStartDateIsRequired, trigger, setValue]);

  const tableData: DataItem[] = useMemo(
    () =>
      (salaryLimits || []).map((salaryLimit) => ({
        values: {
          date: fromIsoDateString(salaryLimit.date, leadingZerosDateFormat),
          title: salaryLimit.value,
        },
        id: salaryLimit.date,
        deletable: true,
        editable: true,
      })),
    [salaryLimits],
  );

  const headers = [
    { text: t("salaryLimitEffectiveDate") },
    { text: t("salaryLimitValueHeader") },
  ];

  const handleOpenEditModal = (_: any, id?: string) => {
    if (id) {
      const index = salaryLimits.findIndex((sl) => sl.date === id);
      setDialogState({ open: true, index: index >= 0 ? index : null });
    } else {
      setDialogState({ open: true, index: null });
    }
  };

  const handleDelete = (id: string) => {
    const index = salaryLimits.findIndex((sl) => sl.date === id);
    if (index >= 0) {
      remove(index);
      if (salaryLimits.length === 1) {
        // Ensure empty list is persisted to payload
        setValue("salaryLimits", []);
        setValue("salaryLimit", "");
      }
    }
  };

  const handleClose = () => setDialogState({ open: false, index: null });

  const title = periodStartDateIsRequired ? (
    t("order:salaryLimitLabel")
  ) : (
    <TitleOptional title={t("order:salaryLimitLabel")} />
  );

  return (
    <FormSection title={title}>
      <FormGridRow>
        <FormTable
          data={tableData}
          headers={headers}
          handleOpenEditModal={handleOpenEditModal}
          addButtonLabel={t("addSalaryLimit")}
          handleDelete={handleDelete}
          translationKeys={["workTitle"]}
        />
      </FormGridRow>
      <FormGridRow
        label={t("periodStartDate")}
        labelSize={3}
        fieldSize={9}
        GridLabelProps={{ mt: 2 }}
        GridFieldProps={{ mt: 2 }}
      >
        <DateField
          name="periodStartDate"
          sx={{ maxWidth: "372px" }}
          required={{ value: !!periodStartDateIsRequired, message: "" }}
          disabled={periodStartDateIsRequired === false}
        />
      </FormGridRow>
      <SalaryLimitsForm
        open={dialogState.open}
        onClose={handleClose}
        index={dialogState.index}
      />
    </FormSection>
  );
};

export default SalaryLimits;
